/**
 * COMPONENT: BedWizard
 * Bulletin d'Engagement Définitif — parcours participant en 6 étapes.
 *
 * Monté par `PublicFormPage` UNIQUEMENT pour un formulaire `generates_bed`
 * ouvert avec un `?token=` : les formulaires BPI classiques gardent
 * strictement leur rendu d'origine.
 *
 * Ce que le composant garantit (cahier des charges + brief vague 1) :
 *   - 6 étapes, frise cliquable, saut direct vers une étape déjà visitée ;
 *   - AUTO-SAVE SERVEUR à chaque changement d'étape et à intervalle régulier :
 *     le participant ferme, revient par le même lien, tout est là ; si tout est
 *     complet et non payé, il atterrit directement sur ⑥ (cahier L94) ;
 *   - échappatoire « je m'engage à communiquer avant le … » sur les
 *     informations critiques, avec mise en avant en en-tête à chaque retour ;
 *   - solde SERVEUR, options branchées sur le socle 00166 ;
 *   - événements `DESERTOURS_BED_STATE` vers le parent à chaque transition,
 *     sans remplacer `DESERTOURS_PAYMENT_READY` (rétro-compatibilité).
 */

import { AlertCircle, ArrowLeft, ArrowRight, Check, CloudUpload, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  notifyParentBedPaymentReady,
  notifyParentBedState,
  type BedMissingItemPayload,
} from '../../lib/analytics';
import {
  finalizeBedSubmission,
  getBedBootstrap,
  saveBedDraft,
  type BedBootstrap,
  type BedCertificate,
  type BedDraftPatch,
  type BedPricingItem,
  type BedSignature,
} from '../../lib/bed/draft';
import { buildBedPrefillValues } from '../../lib/bed/prefill';
import {
  BED_LAST_STEP,
  BED_STEPS,
  buildFieldIndex,
  buildRuleContext,
  clampAmountToPay,
  computeMissingItems,
  computeStepState,
  computeTotalCents,
  deferralKey,
  formatCents,
  groupFieldsByStep,
  isFieldRequired,
  isConditionsAcknowledged,
  isFieldVisible,
  isStepSatisfied,
  landingStep,
  roleKeyOf,
  STUDENT_TRUE_VALUE,
  type BedDeferralGroup,
  type BedDeferralMap,
  type BedStepId,
  type BedStepState,
  type BedValues,
} from '../../lib/bed/wizard-model';
import { useFixedRootFontSize } from '../../lib/bed/use-fixed-root-font-size';
import { createBedPaymentCase } from '../../lib/payment/create-bed-payment-case';
import { supabase } from '../../lib/supabase';
import type { Form, FormFieldWithValues } from '../../types/form';
import { Button } from '../ui';
import { BedDeferrableGroup } from './BedDeferrableGroup';
import { BedFieldInput } from './BedFieldInput';
import { BedMissingBanner } from './BedMissingBanner';
import { BedOptionsStep } from './BedOptionsStep';
import { BedRecapStep, type BedParticipantRole, type BedRecapAmounts } from './BedRecapStep';
import { BedSchoolField } from './BedSchoolField';
import { BedTimeline } from './BedTimeline';

interface BedWizardProps {
  form: Form;
  fields: FormFieldWithValues[];
  token: string;
  /** Mode iframe : le message de fin s'affiche en ligne, sans toast. */
  isEmbedded: boolean;
}

/** Intervalle de sauvegarde automatique en arrière-plan (ms). */
const AUTOSAVE_INTERVAL_MS = 20_000;

/** Ordre des groupes reportables dans une étape participant. */
const DEFERRABLE_ORDER: BedDeferralGroup[] = ['passport', 'permis', 'ceam'];

type WizardPhase = 'loading' | 'ready' | 'submitting' | 'submitted';

type LoadError = 'invalid' | 'expired' | 'error';

interface SubmitOutcome {
  complete: boolean;
  paymentCaseId: string | null;
  missingCount: number;
  amountToPayCents: number | null;
  degraded: 'no_parent' | 'failed' | null;
}

export function BedWizard({ form, fields, token, isEmbedded }: BedWizardProps) {
  // Le CRM dimensionne sa racine en `vw` (maquette 1920 px) : sur un téléphone,
  // tout deviendrait illisible. Le bulletin se dimensionne comme une page web
  // normale, et rend la main en sortant.
  useFixedRootFontSize();

  const [phase, setPhase] = useState<WizardPhase>('loading');
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [bootstrap, setBootstrap] = useState<BedBootstrap | null>(null);

  const [values, setValues] = useState<BedValues>({});
  const [deferred, setDeferred] = useState<BedDeferralMap>({});
  const [signatures, setSignatures] = useState<Partial<Record<BedParticipantRole, BedSignature>>>(
    {}
  );
  const [certificates, setCertificates] = useState<
    Partial<Record<BedParticipantRole, BedCertificate>>
  >({});
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({});
  const [amountToPayCents, setAmountToPayCents] = useState<number | null>(null);

  const [currentStep, setCurrentStep] = useState<BedStepId>(1);
  const [visited, setVisited] = useState<Set<BedStepId>>(new Set([1]));
  const [showErrors, setShowErrors] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  /** Éléments manquants faisant foi : ceux que le SERVEUR a recalculés. */
  const [serverMissing, setServerMissing] = useState<BedMissingItemPayload[] | null>(null);
  /**
   * Le bulletin a déjà été ouvert au moins une fois (brouillon serveur existant
   * ou première étape enregistrée). Conditionne l'en-tête : le cahier L16 parle
   * d'une mise en avant « quand il RETOURNE sur le BED » — sur un bulletin
   * vierge, lister d'emblée tout ce qui reste à saisir ne met rien en avant.
   */
  const [isReturning, setIsReturning] = useState(false);
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fieldsByStep = useMemo(() => groupFieldsByStep(fields), [fields]);
  const fieldIndex = useMemo(() => buildFieldIndex(fields), [fields]);
  const ruleCtx = useMemo(
    () => buildRuleContext(fields, values, deferred),
    [fields, values, deferred]
  );

  const crewNumber = bootstrap?.prefill?.crew?.crew_number ?? null;
  const infoDeadline = bootstrap?.info_deadline ?? '';

  // -------------------------------------------------------------------
  // RÔLES PRÉSENTS
  // -------------------------------------------------------------------
  // Le copilote n'est proposé à la signature/au certificat que s'il existe
  // vraiment (prefill CRM ou saisie manuelle) — sinon on n'exige rien de lui.
  const roles: BedParticipantRole[] = useMemo(() => {
    const list: BedParticipantRole[] = ['pilot'];
    const hasCopilot =
      Boolean(bootstrap?.prefill?.copilot) ||
      fields.some((f) => f.target_role === 'copilot' && String(values[f.id] ?? '').trim() !== '');
    if (hasCopilot) list.push('copilot');
    return list;
  }, [bootstrap, fields, values]);

  const roleLabels: Record<BedParticipantRole, string> = { pilot: 'Pilote', copilot: 'Copilote' };

  // -------------------------------------------------------------------
  // MONTANTS
  // -------------------------------------------------------------------
  const optionalItems = useMemo(() => bootstrap?.optional_items ?? [], [bootstrap]);
  const mandatoryItems = useMemo(() => bootstrap?.mandatory_items ?? [], [bootstrap]);

  const selectedOptions: BedPricingItem[] = useMemo(
    () => optionalItems.filter((item) => selectedOptionIds.includes(item.pricing_item_id)),
    [optionalItems, selectedOptionIds]
  );

  const amounts: BedRecapAmounts | null = useMemo(() => {
    const solde = bootstrap?.solde;
    if (!solde) return null;
    return {
      prixTotalCents: solde.prix_total_cents,
      acompteCents: solde.acompte_cents,
      reductionCents: (solde as { reduction_cents?: number }).reduction_cents ?? 0,
      soldeCents: solde.solde_cents,
    };
  }, [bootstrap]);

  const totalCents = useMemo(() => {
    if (!amounts) return 0;
    return computeTotalCents({
      soldeCents: amounts.soldeCents,
      optionsCents: selectedOptions.reduce((sum, item) => sum + item.amount_cents, 0),
    });
  }, [amounts, selectedOptions]);

  // -------------------------------------------------------------------
  // ÉTATS DES ÉTAPES
  // -------------------------------------------------------------------

  /** Une case « j'ai pris connaissance » manque-t-elle sur l'étape ⑤ ? */
  const optionsBlocked = useMemo(() => {
    const pendingMandatory = mandatoryItems.some(
      (item) => !isConditionsAcknowledged(item, acknowledged)
    );
    const pendingSelected = selectedOptions.some(
      (item) => !isConditionsAcknowledged(item, acknowledged)
    );
    return pendingMandatory || pendingSelected;
  }, [mandatoryItems, selectedOptions, acknowledged]);

  const signaturesMissing = useMemo(
    () => roles.some((role) => !signatures[role]?.data_url),
    [roles, signatures]
  );

  const stepStates = useMemo(() => {
    const states = {} as Record<BedStepId, BedStepState>;
    for (const step of BED_STEPS) {
      states[step.id] = computeStepState(step.id, fieldsByStep[step.id], ruleCtx, {
        optionsBlocked: step.id === 5 ? optionsBlocked : undefined,
        signaturesMissing: step.id === 6 ? signaturesMissing : undefined,
        extraDeferrals:
          step.id === 6 ? roles.map((role) => deferralKey('certificat', role)) : undefined,
      });
    }
    return states;
  }, [fieldsByStep, ruleCtx, optionsBlocked, signaturesMissing, roles]);

  /**
   * Liste affichée : celle du serveur tant qu'elle est fraîche, sinon le
   * miroir client (mêmes règles) pour un retour immédiat entre deux
   * enregistrements.
   */
  const missingItems = useMemo(() => {
    const local = computeMissingItems(fields, values, deferred);
    if (!serverMissing) return local;
    // Le serveur ne connaît ni les signatures ni le certificat : on garde sa
    // liste (qui fait foi sur les champs) et on la complète des reports
    // hors-formulaire déclarés localement.
    const serverCodes = new Set(serverMissing.map((item) => item.code));
    return [...serverMissing, ...local.filter((item) => !serverCodes.has(item.code))];
  }, [fields, values, deferred, serverMissing]);

  // -------------------------------------------------------------------
  // CHARGEMENT
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const result = await getBedBootstrap(token);
      if (cancelled) return;

      if (result.status !== 'ok') {
        setLoadError(result.status === 'error' ? 'error' : (result.status as LoadError));
        setPhase('ready');
        return;
      }

      const data = result.data;
      setBootstrap(data);

      // Pré-remplissage CRM d'abord, brouillon ENSUITE : ce que le participant
      // a saisi prime toujours sur ce que le CRM croit savoir.
      const prefilled = buildBedPrefillValues(fields, data.prefill);
      const draftValues = data.draft?.values ?? {};
      setValues({ ...prefilled, ...draftValues });
      setDeferred(data.draft?.deferred ?? {});
      setSignatures(data.draft?.signatures ?? {});
      setCertificates(data.draft?.medical_certificates ?? {});
      setSelectedOptionIds(data.draft?.options?.selected_item_ids ?? []);
      setAcknowledged(data.draft?.options?.acknowledged ?? {});
      setAmountToPayCents(data.draft?.amount_to_pay_cents ?? null);
      setServerMissing(data.draft?.missing_items ?? null);
      setIsReturning(Boolean(data.draft));

      if (data.draft?.submitted_at) {
        setPhase('submitted');
        setOutcome({
          complete: (data.draft.missing_items ?? []).length === 0,
          paymentCaseId: data.draft.payment_case_id,
          missingCount: (data.draft.missing_items ?? []).length,
          amountToPayCents: data.draft.amount_to_pay_cents,
          degraded: null,
        });
        setCurrentStep(BED_LAST_STEP);
        setVisited(new Set(BED_STEPS.map((s) => s.id)));
        return;
      }

      setPhase('ready');
      setVisited(new Set(BED_STEPS.map((s) => s.id).filter((id) => id <= (data.draft ? 6 : 1))));
    })();

    return () => {
      cancelled = true;
    };
    // `fields` est stable une fois le formulaire chargé (PublicFormPage le fige).
  }, [token, fields]);

  /**
   * Atterrissage (cahier L94) : à la reprise d'un brouillon, on ouvre la
   * première étape incomplète — et directement le récapitulatif si tout est
   * complet. Calculé une seule fois, après le chargement.
   */
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current || phase !== 'ready' || !bootstrap) return;
    landedRef.current = true;
    if (!bootstrap.draft) return;
    setCurrentStep(landingStep(stepStates));
  }, [phase, bootstrap, stepStates]);

  // -------------------------------------------------------------------
  // ÉVÉNEMENT `loaded`
  // -------------------------------------------------------------------
  const loadedNotifiedRef = useRef(false);
  useEffect(() => {
    if (loadedNotifiedRef.current || !bootstrap) return;
    loadedNotifiedRef.current = true;
    notifyParentBedState({
      state: 'loaded',
      step: currentStep,
      crewNumber,
      missingItems: bootstrap.draft?.missing_items ?? [],
      // 00178 : statut CRM après l'ouverture du lien — `get_bed_bootstrap` a
      // déjà posé « BED téléchargé » si le porteur était pré-inscrit.
      bedStatus: bootstrap.bed_status,
    });
  }, [bootstrap, crewNumber, currentStep]);

  // -------------------------------------------------------------------
  // AUTO-SAVE
  // -------------------------------------------------------------------

  /** Snapshot courant, lu par la sauvegarde sans la faire dépendre du rendu. */
  const snapshotRef = useRef<BedDraftPatch>({});
  snapshotRef.current = {
    values,
    deferred,
    step_states: stepStates,
    last_step: currentStep,
    signatures,
    options: { selected_item_ids: selectedOptionIds, acknowledged },
    medical_certificates: certificates,
    amount_to_pay_cents: amountToPayCents,
  };

  const persist = useCallback(
    async (options: { emitStep?: BedStepId } = {}) => {
      if (phase === 'submitted' || phase === 'submitting') return true;
      setSaveState('saving');

      const result = await saveBedDraft(token, snapshotRef.current);

      if (result.status !== 'ok') {
        setSaveState('error');
        return false;
      }

      setSaveState('saved');
      setLastSavedAt(result.savedAt);
      setServerMissing(result.missingItems);
      setIsReturning(true);

      if (options.emitStep !== undefined) {
        notifyParentBedState({
          state: 'step_saved',
          step: options.emitStep,
          crewNumber,
          missingItems: result.missingItems,
        });
      }
      return true;
    },
    [phase, token, crewNumber]
  );

  // Sauvegarde périodique en arrière-plan : une fermeture d'onglet inopinée ne
  // coûte au pire que les 20 dernières secondes de saisie.
  useEffect(() => {
    if (phase !== 'ready') return;
    const handle = window.setInterval(() => {
      void persist();
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [phase, persist]);

  // -------------------------------------------------------------------
  // NAVIGATION
  // -------------------------------------------------------------------
  const goToStep = useCallback(
    async (target: BedStepId, opts: { save?: boolean } = { save: true }) => {
      if (opts.save !== false) await persist({ emitStep: currentStep });
      setShowErrors(false);
      setCurrentStep(target);
      setVisited((prev) => new Set(prev).add(target));
      // Sur mobile, on remonte : sinon on atterrit au milieu de l'étape suivante.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [persist, currentStep]
  );

  const handleNext = async () => {
    if (!isStepSatisfied(stepStates[currentStep])) {
      setShowErrors(true);
      await persist({ emitStep: currentStep });
      return;
    }
    if (currentStep < BED_LAST_STEP) {
      await goToStep((currentStep + 1) as BedStepId);
    }
  };

  const handlePrevious = async () => {
    if (currentStep > 1) await goToStep((currentStep - 1) as BedStepId);
  };

  // -------------------------------------------------------------------
  // MUTATIONS
  // -------------------------------------------------------------------
  const handleFieldChange = useCallback(
    (fieldId: string, value: string) => {
      setValues((prev) => {
        const next = { ...prev, [fieldId]: value };

        // Bascule étudiant / jeune actif : on VIDE le champ devenu sans objet.
        // Sans cela, une profession pré-remplie par le CRM survit à un
        // re-basculement en « étudiant », et le trigger SQL
        // `create_contact_from_submission` — qui déduit « plus étudiant » de la
        // seule présence d'une profession — refuse alors de rattacher l'école.
        const role = Object.entries(fieldIndex.student).find(([, f]) => f.id === fieldId)?.[0];
        if (role) {
          const becameNotStudent = value === STUDENT_TRUE_VALUE;
          const toClear = becameNotStudent
            ? [fieldIndex.school[role], fieldIndex.schoolCity[role]]
            : [fieldIndex.profession[role]];
          toClear.forEach((f) => {
            if (f) next[f.id] = '';
          });
        }

        return next;
      });
    },
    [fieldIndex]
  );

  const handleToggleDeferral = (group: BedDeferralGroup, role: string, on: boolean) => {
    const key = deferralKey(group, role);
    setDeferred((prev) => {
      const next = { ...prev };
      if (on) next[key] = { due_date: infoDeadline };
      else delete next[key];
      return next;
    });
  };

  const handleDeferralDate = (group: BedDeferralGroup, role: string, dueDate: string) => {
    const key = deferralKey(group, role);
    setDeferred((prev) => (prev[key] ? { ...prev, [key]: { due_date: dueDate } } : prev));
  };

  const handleToggleOption = (item: BedPricingItem, selected: boolean) => {
    setSelectedOptionIds((prev) =>
      selected
        ? [...new Set([...prev, item.pricing_item_id])]
        : prev.filter((id) => id !== item.pricing_item_id)
    );
  };

  const handleToggleAck = (item: BedPricingItem, acked: boolean) => {
    setAcknowledged((prev) => {
      const next = { ...prev };
      // On mémorise l'URL AFFICHÉE : le socle 00166 n'accepte la preuve que si
      // elle correspond au document courant (anti-course sur la rotation).
      if (acked && item.conditions_url) next[item.pricing_item_id] = item.conditions_url;
      else delete next[item.pricing_item_id];
      return next;
    });
    // Décocher le document retire l'option : on ne garde jamais une option
    // souscrite sans preuve de lecture.
    if (!acked) {
      setSelectedOptionIds((prev) => prev.filter((id) => id !== item.pricing_item_id));
    }
  };

  const handleSignature = (role: BedParticipantRole, dataUrl: string | null) => {
    setSignatures((prev) => {
      const next = { ...prev };
      if (dataUrl) next[role] = { data_url: dataUrl, signed_at: new Date().toISOString() };
      else delete next[role];
      return next;
    });
  };

  const handleCertificate = (role: BedParticipantRole, certificate: BedCertificate | null) => {
    setCertificates((prev) => {
      const next = { ...prev };
      if (certificate) next[role] = certificate;
      else delete next[role];
      return next;
    });
  };

  // -------------------------------------------------------------------
  // SOUMISSION
  // -------------------------------------------------------------------
  const buildSubmissionData = (role: 'pilot' | 'copilot') => {
    const data: Record<string, unknown> = {};
    for (const field of fields) {
      const isRole = field.target_role === role;
      const isCommon = !field.target_role;
      if (!isRole && !isCommon) continue;
      if (values[field.id] !== undefined) data[field.id] = values[field.id];
    }

    // Métadonnées BED sous un préfixe réservé : le trigger
    // `create_contact_from_submission` n'itère que sur les `form_fields`, ces
    // clés lui sont donc invisibles. C'est là que la vague 2 (zone admin) ira
    // lire les éléments manquants, la signature et les engagements de date.
    data.__bed_role = role;
    data.__bed_crew_number = crewNumber;
    data.__bed_deferred = deferred;
    data.__bed_missing_items = missingItems;
    data.__bed_signature = signatures[role] ?? null;
    data.__bed_medical_certificate = certificates[role] ?? null;
    data.__bed_selected_option_ids = selectedOptionIds;
    data.__bed_acknowledged_conditions = acknowledged;
    data.__bed_amount_to_pay_cents = amountToPayCents ?? totalCents;

    return data;
  };

  const handleSubmit = async () => {
    if (phase === 'submitting') return;
    if (signaturesMissing || optionsBlocked) {
      setShowErrors(true);
      return;
    }

    setPhase('submitting');
    setSubmitError(null);

    // Le brouillon est enregistré AVANT toute écriture définitive : si la
    // chaîne de paiement échoue, la saisie n'est jamais perdue.
    await persist();

    try {
      const submissionIds: string[] = [];

      for (const role of roles) {
        const { data, error } = await supabase
          .from('form_submissions')
          .insert({ form_id: form.id, data: buildSubmissionData(role) })
          .select()
          .single();

        if (error) throw error;
        submissionIds.push(data.id);

        // Notification email : best-effort, exactement comme le renderer public.
        supabase.functions
          .invoke('notify-form-submission', {
            body: { form_id: form.id, submission_id: data.id, submission_data: data.data },
          })
          .catch((err) => console.warn('[BED] notification email:', err));
      }

      const paymentResult = await createBedPaymentCase({
        formSubmissionId: submissionIds[0],
        bedAccessToken: token,
      });

      if (paymentResult.status !== 'ok') {
        // Le bulletin EST enregistré : on ne le perd pas parce que le solde
        // n'a pas pu être initialisé. On le dit, et on oriente vers le support.
        setOutcome({
          complete: missingItems.length === 0,
          paymentCaseId: null,
          missingCount: missingItems.length,
          amountToPayCents: null,
          degraded: paymentResult.status === 'no_paid_bpi_parent' ? 'no_parent' : 'failed',
        });
        setPhase('submitted');
        notifyParentBedState({
          state: missingItems.length === 0 ? 'submitted_complete' : 'submitted_incomplete',
          crewNumber,
          missingItems: missingItems as BedMissingItemPayload[],
        });
        return;
      }

      const paymentCase = paymentResult.data;

      const finalized = await finalizeBedSubmission({
        token,
        paymentCaseId: paymentCase.payment_case_id,
        submissionIds,
        optionItemIds: selectedOptionIds,
        conditionsUrls: acknowledged,
        amountToPayCents,
      });

      const finalMissing =
        finalized.status === 'ok'
          ? (finalized.missingItems as BedMissingItemPayload[])
          : (missingItems as BedMissingItemPayload[]);
      const complete = finalized.status === 'ok' ? finalized.complete : finalMissing.length === 0;
      const finalAmount =
        finalized.status === 'ok' ? finalized.amountToPayCents : (amountToPayCents ?? totalCents);
      // 00178 : statut CRM après la transition de soumission, tel que le
      // serveur l'a posé. `null` si la finalisation a échoué — la clé est alors
      // absente du message, jamais devinée côté client.
      const finalStatus = finalized.status === 'ok' ? finalized.bedStatus : null;

      setServerMissing(finalMissing);
      setOutcome({
        complete,
        paymentCaseId: paymentCase.payment_case_id,
        missingCount: finalMissing.length,
        amountToPayCents: finalAmount,
        degraded: null,
      });
      setPhase('submitted');

      notifyParentBedState({
        state: complete ? 'submitted_complete' : 'submitted_incomplete',
        crewNumber,
        missingItems: finalMissing,
        paymentCaseId: paymentCase.payment_case_id,
        amountToPayCents: finalAmount,
        bedStatus: finalStatus,
      });

      // Rétro-compatibilité : le message historique reste émis À L'IDENTIQUE,
      // le nouvel événement `payment_ready` s'y ajoute.
      notifyParentBedPaymentReady({
        paymentCaseId: paymentCase.payment_case_id,
        product: form.product?.code ?? paymentCase.product_code ?? '',
        bedToken: paymentCase.bed_token,
        orderId: paymentCase.order_id,
      });

      notifyParentBedState({
        state: 'payment_ready',
        crewNumber,
        paymentCaseId: paymentCase.payment_case_id,
        bedToken: paymentCase.bed_token,
        orderId: paymentCase.order_id,
        amountToPayCents: finalAmount,
        missingItems: finalMissing,
        bedStatus: finalStatus,
      });
    } catch (err) {
      console.error('[BED] Erreur de soumission:', err);
      setSubmitError(
        'Votre bulletin n’a pas pu être envoyé. Vos réponses sont enregistrées : réessayez dans un instant.'
      );
      setPhase('ready');
    }
  };

  // -------------------------------------------------------------------
  // RENDU — champs d'une étape
  // -------------------------------------------------------------------
  const renderStepFields = (step: BedStepId) => {
    const stepFields = fieldsByStep[step].filter((field) => isFieldVisible(field, ruleCtx));
    const grouped = new Map<string, FormFieldWithValues[]>();
    const plain: FormFieldWithValues[] = [];

    for (const field of stepFields) {
      if (field.bed_deferrable_group) {
        const key = `${field.bed_deferrable_group}:${roleKeyOf(field.target_role)}`;
        const bucket = grouped.get(key) ?? [];
        bucket.push(field);
        grouped.set(key, bucket);
      } else {
        plain.push(field);
      }
    }

    const renderField = (field: FormFieldWithValues) => {
      const required = isFieldRequired(field, ruleCtx);
      const empty = String(values[field.id] ?? '').trim() === '';
      const error = showErrors && required && empty ? `${field.label} est requis` : null;

      // L'école et sa ville sont rendues ensemble (autocomplete + création).
      if (field.contact_field_map === 'school') {
        const role = roleKeyOf(field.target_role);
        const cityField = fieldIndex.schoolCity[role];
        return (
          <BedSchoolField
            key={field.id}
            token={token}
            schoolField={field}
            cityField={cityField}
            schoolValue={values[field.id] ?? ''}
            cityValue={cityField ? (values[cityField.id] ?? '') : ''}
            cityRequired={cityField ? isFieldRequired(cityField, ruleCtx) : false}
            onChange={handleFieldChange}
            disabled={phase === 'submitting'}
          />
        );
      }

      // La ville d'école est déjà affichée par BedSchoolField.
      if (field.contact_field_map === 'school_city') return null;

      return (
        <BedFieldInput
          key={field.id}
          field={field}
          values={values}
          required={required}
          error={error}
          disabled={phase === 'submitting'}
          onChange={handleFieldChange}
        />
      );
    };

    // Les groupes reportables se placent après les champs libres, dans l'ordre
    // passeport → permis → CEAM, pour que l'identité vienne d'abord.
    const groupKeys = [...grouped.keys()].sort((a, b) => {
      const ga = DEFERRABLE_ORDER.indexOf(a.split(':')[0] as BedDeferralGroup);
      const gb = DEFERRABLE_ORDER.indexOf(b.split(':')[0] as BedDeferralGroup);
      return ga - gb;
    });

    return (
      <div className="space-y-5">
        {plain.map(renderField)}

        {groupKeys.map((key) => {
          const [group, role] = key.split(':') as [BedDeferralGroup, string];
          const entry = deferred[key];
          return (
            <BedDeferrableGroup
              key={key}
              group={group}
              role={role}
              deferredUntil={entry?.due_date ?? null}
              defaultDueDate={infoDeadline}
              disabled={phase === 'submitting'}
              onToggle={(on) => handleToggleDeferral(group, role, on)}
              onDueDateChange={(date) => handleDeferralDate(group, role, date)}
            >
              {(grouped.get(key) ?? []).map(renderField)}
            </BedDeferrableGroup>
          );
        })}
      </div>
    );
  };

  // -------------------------------------------------------------------
  // RENDU — états globaux
  // -------------------------------------------------------------------
  if (phase === 'loading') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-zinc-400" />
          <p className="text-sm text-zinc-600">Chargement de votre bulletin…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-amber-600" />
        <h2 className="mb-2 text-lg font-semibold text-amber-900">
          {loadError === 'expired' ? 'Ce lien a expiré' : 'Ce lien n’est plus valide'}
        </h2>
        <p className="text-sm text-amber-800">
          Contactez-nous à{' '}
          <a href="mailto:service.administratif@desertours.com" className="font-medium underline">
            service.administratif@desertours.com
          </a>{' '}
          pour recevoir un nouveau lien vers votre bulletin.
        </p>
      </div>
    );
  }

  if (phase === 'submitted' && outcome) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div
          className={`rounded-lg border p-6 text-center ${
            outcome.complete ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="mb-3 text-4xl">{outcome.complete ? '✓' : '⚠️'}</div>
          <h2 className="mb-2 text-xl font-semibold text-zinc-900">
            {outcome.complete
              ? 'Votre bulletin est complet et enregistré'
              : 'Votre bulletin est enregistré'}
          </h2>
          <p className="text-sm text-zinc-700">
            {outcome.complete
              ? 'Il ne vous reste plus qu’à finaliser votre paiement.'
              : `Il reste ${outcome.missingCount} élément${
                  outcome.missingCount > 1 ? 's' : ''
                } à nous transmettre. Vous pouvez revenir sur ce lien à tout moment.`}
          </p>
          {outcome.amountToPayCents !== null && (
            <p className="mt-3 text-sm font-medium text-zinc-900">
              Montant à régler : {formatCents(outcome.amountToPayCents)}
            </p>
          )}
        </div>

        {outcome.degraded && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm">
            <p className="font-medium text-amber-800">
              {outcome.degraded === 'no_parent'
                ? 'Aucun acompte réglé n’a été retrouvé pour votre équipage : le solde ne peut pas être calculé automatiquement.'
                : 'Le paiement du solde n’a pas pu être initié automatiquement.'}
            </p>
            <p className="mt-1 text-amber-700">
              Écrivez-nous à{' '}
              <a
                href="mailto:service.administratif@desertours.com"
                className="font-medium underline"
              >
                service.administratif@desertours.com
              </a>{' '}
              pour régulariser votre dossier.
            </p>
          </div>
        )}

        <BedMissingBanner items={missingItems} submitted />
      </div>
    );
  }

  const step = BED_STEPS.find((s) => s.id === currentStep)!;
  const isLastStep = currentStep === BED_LAST_STEP;

  return (
    <div className="space-y-6">
      {/* En-tête du BED : éléments manquants mis en avant à chaque retour. */}
      {isReturning && <BedMissingBanner items={missingItems} />}

      <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-5">
        <BedTimeline
          current={currentStep}
          states={stepStates}
          visited={visited}
          onNavigate={(target) => void goToStep(target)}
        />
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
        <header className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Étape {step.index} sur {BED_STEPS.length}
            </p>
            <h2 className="text-lg font-semibold text-zinc-900">{step.title}</h2>
            <p className="text-sm text-zinc-600">{step.hint}</p>
          </div>

          <span className="flex items-center gap-1.5 text-xs text-zinc-500" aria-live="polite">
            {saveState === 'saving' && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enregistrement…
              </>
            )}
            {saveState === 'saved' && lastSavedAt && (
              <>
                <CloudUpload className="h-3.5 w-3.5 text-emerald-600" />
                Enregistré à{' '}
                {new Date(lastSavedAt).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </>
            )}
            {saveState === 'error' && (
              <span className="text-amber-700">Sauvegarde impossible — réessai automatique</span>
            )}
          </span>
        </header>

        {currentStep === 1 && crewNumber && (
          <p className="mb-5 rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
            Votre numéro d’équipage : <span className="font-semibold">{crewNumber}</span>
          </p>
        )}

        {currentStep === 5 ? (
          <BedOptionsStep
            mandatoryItems={mandatoryItems}
            optionalItems={optionalItems}
            selectedIds={selectedOptionIds}
            acknowledged={acknowledged}
            onToggleOption={handleToggleOption}
            onToggleAck={handleToggleAck}
            consentFields={fieldsByStep[5].filter((f) => isFieldVisible(f, ruleCtx))}
            values={values}
            isRequired={(field) => isFieldRequired(field, ruleCtx)}
            onChangeField={handleFieldChange}
            disabled={phase === 'submitting'}
          />
        ) : currentStep === 6 ? (
          <BedRecapStep
            token={token}
            amounts={amounts}
            selectedOptions={selectedOptions}
            totalCents={totalCents}
            amountToPayCents={amountToPayCents}
            onAmountToPayChange={(cents) =>
              setAmountToPayCents(clampAmountToPay(cents, totalCents))
            }
            roles={roles}
            roleLabels={roleLabels}
            signatures={signatures}
            onSignatureChange={handleSignature}
            certificates={certificates}
            onCertificateChange={handleCertificate}
            deferred={deferred}
            onToggleCertificateDeferral={(role, on) => handleToggleDeferral('certificat', role, on)}
            defaultDueDate={infoDeadline}
            disabled={phase === 'submitting'}
          />
        ) : (
          renderStepFields(currentStep)
        )}

        {showErrors && !isStepSatisfied(stepStates[currentStep]) && (
          <p className="mt-5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {currentStep === 5
              ? 'Prenez connaissance des documents obligatoires avant de continuer.'
              : currentStep === 6
                ? 'Chaque participant doit signer avant l’envoi du bulletin.'
                : 'Complétez les champs obligatoires, ou cochez « je m’engage à communiquer avant le … ».'}
          </p>
        )}

        {submitError && (
          <p className="mt-5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {submitError}
          </p>
        )}

        {/* Navigation : empilée sur mobile, alignée sur desktop. */}
        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-zinc-100 pt-5 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handlePrevious()}
            disabled={currentStep === 1 || phase === 'submitting'}
          >
            <ArrowLeft className="h-4 w-4" />
            Précédent
          </Button>

          {isLastStep ? (
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={phase === 'submitting'}
              data-testid="bed-submit"
            >
              {phase === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Envoyer mon bulletin
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleNext()}
              disabled={phase === 'submitting'}
            >
              Continuer
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!isEmbedded && (
        <p className="text-center text-xs text-zinc-500">
          Vos réponses sont enregistrées automatiquement : vous pouvez fermer cette page et revenir
          plus tard avec le même lien.
        </p>
      )}
    </div>
  );
}
